import type { FC } from 'react';

import { useNotificationsPage } from '@/features/notifications/hooks/useNotificationsPage.hook';
import type { CampaignStatus } from '@/types/notification.types';

const inputBase =
  'w-full rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-3 py-2 text-[13px] text-[var(--color-ink)] outline-none transition-colors placeholder:text-[var(--color-ink-muted)] focus:border-brand-400 focus:ring-2 focus:ring-brand-100';

const STATUS_STYLES: Record<CampaignStatus, string> = {
  sent: 'bg-[var(--color-success-bg)] text-[var(--color-success-ink)]',
  scheduled: 'bg-brand-50 text-brand-600',
  draft: 'bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]',
};

const NotificationsPage: FC = () => {
  const vm = useNotificationsPage();

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--color-ink)]">
          Notifications
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
          Send a push notification to all users or a specific campus.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Composer */}
        <section className="flex flex-col gap-4 rounded-2xl border border-[var(--color-divider)] bg-white p-5">
          <h2 className="text-[15px] font-bold text-[var(--color-ink)]">New campaign</h2>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
              Heading <span className="text-[var(--color-danger-ink)]">*</span>
            </label>
            <input
              type="text"
              maxLength={80}
              className={inputBase}
              placeholder="e.g. Tech Fest is live!"
              value={vm.form.title}
              onChange={(e) => vm.onChangeField('title', e.target.value)}
            />
            {vm.errors.title ? (
              <p className="text-[11px] font-medium text-[var(--color-danger-ink)]">
                {vm.errors.title}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">
              Message <span className="text-[var(--color-danger-ink)]">*</span>
            </label>
            <textarea
              rows={3}
              maxLength={500}
              className={`${inputBase} resize-none`}
              placeholder="What do you want to tell students?"
              value={vm.form.message}
              onChange={(e) => vm.onChangeField('message', e.target.value)}
            />
            {vm.errors.message ? (
              <p className="text-[11px] font-medium text-[var(--color-danger-ink)]">
                {vm.errors.message}
              </p>
            ) : null}
          </div>

          {/* Audience */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">Audience</label>
            <div className="flex gap-2">
              {(['all', 'campus'] as const).map((scope) => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => vm.onChangeScope(scope)}
                  className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${
                    vm.form.scope === scope
                      ? 'border-brand-500 bg-brand-500 text-white'
                      : 'border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:border-brand-300 hover:text-brand-600'
                  }`}
                >
                  {scope === 'all' ? 'All users' : 'Specific campus'}
                </button>
              ))}
            </div>

            {vm.form.scope === 'campus' ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {vm.campusOptions.map((campus) => {
                  const selected = vm.form.campusIds.includes(campus.id);
                  return (
                    <button
                      key={campus.id}
                      type="button"
                      onClick={() => vm.onToggleCampus(campus.id)}
                      className={`rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                        selected
                          ? 'border-brand-400 bg-brand-50 text-brand-600'
                          : 'border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:border-brand-300'
                      }`}
                    >
                      {campus.name}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {vm.errors.campusIds ? (
              <p className="text-[11px] font-medium text-[var(--color-danger-ink)]">
                {vm.errors.campusIds}
              </p>
            ) : null}
          </div>

          {/* Schedule */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-semibold text-[var(--color-ink-muted)]">When</label>
            <div className="flex gap-2">
              {(['now', 'later'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => vm.onChangeScheduleMode(mode)}
                  className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors ${
                    vm.form.scheduleMode === mode
                      ? 'border-brand-500 bg-brand-500 text-white'
                      : 'border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:border-brand-300 hover:text-brand-600'
                  }`}
                >
                  {mode === 'now' ? 'Send now' : 'Schedule'}
                </button>
              ))}
            </div>
            {vm.form.scheduleMode === 'later' ? (
              <input
                type="datetime-local"
                className={inputBase}
                value={vm.form.sendAt}
                onChange={(e) => vm.onChangeSendAt(e.target.value)}
              />
            ) : null}
            {vm.errors.sendAt ? (
              <p className="text-[11px] font-medium text-[var(--color-danger-ink)]">
                {vm.errors.sendAt}
              </p>
            ) : null}
          </div>

          {vm.submitError ? (
            <div className="rounded-lg border border-[var(--color-danger-ink)]/30 bg-[var(--color-danger-bg)] px-3 py-2 text-[12px] font-medium text-[var(--color-danger-ink)]">
              {vm.submitError}
            </div>
          ) : null}
          {vm.successMessage ? (
            <div className="rounded-lg border border-[var(--color-success-ink)]/30 bg-[var(--color-success-bg)] px-3 py-2 text-[12px] font-medium text-[var(--color-success-ink)]">
              {vm.successMessage}
            </div>
          ) : null}

          <button
            type="button"
            onClick={vm.onSubmit}
            disabled={vm.isSending}
            className="self-start rounded-lg bg-brand-500 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {vm.isSending
              ? 'Sending…'
              : vm.form.scheduleMode === 'later'
                ? 'Schedule campaign'
                : 'Send campaign'}
          </button>
        </section>

        {/* Recent campaigns */}
        <section className="flex flex-col gap-3 rounded-2xl border border-[var(--color-divider)] bg-white p-5">
          <h2 className="text-[15px] font-bold text-[var(--color-ink)]">Recent campaigns</h2>

          {vm.isLoadingList ? (
            <p className="py-8 text-center text-[13px] text-[var(--color-ink-muted)]">Loading…</p>
          ) : vm.campaigns.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-[var(--color-ink-muted)]">
              No campaigns yet.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[var(--color-divider)]">
              {vm.campaigns.map((campaign) => (
                <li key={campaign.id} className="flex flex-col gap-1 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-[13px] font-bold text-[var(--color-ink)]">{campaign.title}</p>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLES[campaign.status]}`}
                    >
                      {campaign.status}
                    </span>
                  </div>
                  <p className="line-clamp-2 text-[12px] text-[var(--color-ink-muted)]">
                    {campaign.message}
                  </p>
                  <p className="text-[11px] text-[var(--color-ink-muted)]">
                    {campaign.audience.scope === 'all'
                      ? 'All users'
                      : `${campaign.audience.campusIds.length} campus${campaign.audience.campusIds.length === 1 ? '' : 'es'}`}
                    {campaign.status === 'sent'
                      ? ` · ${campaign.recipients.toLocaleString()} recipients`
                      : campaign.sendAt
                        ? ` · scheduled ${new Date(campaign.sendAt).toLocaleString()}`
                        : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default NotificationsPage;
